import { Injectable, inject } from '@angular/core';
import { type ActivatedRouteSnapshot, Router, type UrlTree } from '@angular/router';
import { stripLanguageFromUrl } from '@reconcept-platform/shared/util';
import { type Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthApiService } from './auth.api';
import { WebSessionService } from './session/web-session.service';

@Injectable({
  providedIn: 'root',
})
export class SuccessfulRedirectGuard {
  private session = inject(WebSessionService);
  private authApi = inject(AuthApiService);
  private router = inject(Router);

  canActivate(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.isSuccessfulRedirect(route);
  }

  // todo: needs beUrl to be added?
  // so the login application can verify at the correct server?
  private isSuccessfulRedirect(route: ActivatedRouteSnapshot) {
    const queryParams = route.queryParams;
    // we expect queryparameters for authorization
    return this.session.requestAndSetAccessToken(queryParams.code).pipe(
      map(() => ({
        data: route.data,
        state: queryParams.state,
      })),
      map(({ state, data }) => {
        if (!data.internalRedirectPath) {
          throw Error('No internalRedirectPath was set on route.data!');
        }

        const showSecondaryEmailModal = 'showSecondaryEmailModal';
        // redirect
        // to internal redirect uri
        if (state) {
          let returnState = this.authApi.decodeState(state);

          // temporary to show the secondary-email-modal for a while
          returnState = this.addSecondaryEmailModalParam(returnState, showSecondaryEmailModal);
          returnState = stripLanguageFromUrl(returnState);
          // go to remembered state, we use navigateBy
          // Url to parse any queryParams that might be present
          this.router.navigateByUrl(returnState);
        } else {
          // go to default path

          this.router.navigate(data.internalRedirectPath, { queryParams: { showSecondaryEmailModal: true } });
        }

        return false;
      }),
      catchError(() => {
        this.session.redirectToLogin(this.authApi.decodeState(queryParams.state), queryParams);
        return of(false);
      }),
    );
  }

  private addSecondaryEmailModalParam(returnState: string, paramKey: string) {
    const activatedParamKey = paramKey + '=true';
    if (returnState.includes('?')) {
      returnState += '&' + activatedParamKey;
    } else {
      returnState += '?' + activatedParamKey;
    }
    return returnState;
  }
}
